/* ==============================================================================
   Motor de voz — a peça que junta tudo
   ==============================================================================
   Gateway + malha WebRTC + pipeline de áudio + governador de banda, por trás de
   uma API que a interface consegue usar sem saber de nenhum dos quatro.

   Por que existe uma camada de orquestração
   ========================================
   Sem ela, o componente de React precisaria: assinar sete eventos do gateway,
   criar a malha na hora certa, casar peer com track, iniciar o pipeline no gesto
   do usuário, alimentar o governador com o estado da malha, e desmontar tudo na
   ordem inversa. Isso é lógica de domínio, não de apresentação — e escrita num
   componente ela seria refeita no Electron.

   A ordem de montagem, que não é arbitrária
   =========================================
     1. pipeline de áudio PRIMEIRO, dentro do gesto do usuário
        (`AudioContext` nasce suspenso e só `resume()` no gesto)
     2. VOICE_JOIN no gateway, que devolve roster e credenciais ICE
     3. malha criada com as credenciais recebidas
     4. um par por integrante do roster
     5. governador ligado por último, quando já há o que governar

   Inverter 1 e 2 produz o defeito mais confuso do conjunto: entra na sala, o
   roster aparece, e ninguém ouve nada — porque o contexto de áudio ficou
   suspenso e o track de saída é silêncio.
   ============================================================================== */

import type { VoicePeer } from '@concord/shared';

import type { GatewayClient } from '../gateway/client.ts';
import { SpeakingDetector } from '../webrtc/speaking.ts';
import {
  AudioPipeline,
  DEFAULT_AUDIO_SETTINGS,
  type AudioLevel,
  type AudioSettings,
} from '../audio/pipeline.ts';
import { BandwidthGovernor, type AllocationDecision } from '../webrtc/bandwidth.ts';
import {
  VoiceMesh,
  type ControlMessage,
  type LocalTrack,
  type RemoteTrack,
} from '../webrtc/mesh.ts';
import {
  CaptureCancelled,
  captureCamera,
  captureScreen,
  type CaptureOptions,
} from '../webrtc/screen-capture.ts';
import { detectCapabilities } from '../platform/capabilities.ts';

export interface VoiceParticipant {
  userId: string;
  sessionId: string;
  /*
   * Nome vindo do ROSTER do gateway, nao resolvido no cliente.
   *
   * Existe porque a interface antes resolvia nome na lista de membros do REST —
   * carregada uma vez, ao abrir o servidor. Quem entrava na voz depois nao
   * estava nela e aparecia como "Participante". Ver `voicePeerSchema`.
   *
   * Ausente em `isSelf`: o proprio nome vem da sessao, que e melhor fonte.
   */
  username?: string | undefined;
  displayName?: string | null | undefined;
  /** `true` para o próprio usuário. */
  isSelf: boolean;
  selfMuted: boolean;
  selfDeafened: boolean;
  cameraOn: boolean;
  screenCount: number;
  /** Tracks recebidos deste par. */
  tracks: RemoteTrack[];
  connectionState: RTCPeerConnectionState | 'self';
  /** `true` quando a conexão está instável mas não perdida. */
  unstable: boolean;
  /*
   * Está falando AGORA.
   *
   * Até esta versão o campo não existia e a interface usava `false` fixo em
   * todos os lugares que desenhavam o anel de "falando" — o desenho estava
   * pronto e nada nunca o acendia.
   *
   * Para pares, vem do `audioLevel` do cabeçalho RTP (ver `webrtc/speaking.ts`).
   * Para si mesmo, do medidor do próprio pipeline, que é mais preciso porque o
   * sinal ainda não passou por codificação.
   */
  speaking: boolean;
  /** 0..1. Para intensidade de brilho, não só liga/desliga. */
  speakingLevel: number;
}

export interface VoiceEngineState {
  channelId: string | null;
  connected: boolean;
  participants: VoiceParticipant[];
  micEnabled: boolean;
  deafened: boolean;
  cameraOn: boolean;
  screens: LocalTrack[];
  /*
   * A faixa da PRÓPRIA câmera, e não só `cameraOn: boolean`.
   *
   * Sem ela a interface sabia que a câmera estava ligada e não tinha o que
   * mostrar: o card do próprio usuário continuava com as iniciais enquanto a
   * luz da webcam estava acesa. Quem compartilha precisa VER o que está
   * enviando — é a mesma razão pela qual as telas locais já vinham aqui.
   */
  camera: LocalTrack | null;
  audioLevel: AudioLevel;
  bandwidth: AllocationDecision | null;
  notice: string | null;
  maxParticipants: number;
}

export interface VoiceEngineOptions {
  gateway: GatewayClient;
  audioSettings?: AudioSettings;
  /** Teto de upload em kbps. `null` = usar a estimativa medida. */
  uploadCeilingKbps?: number | null;
  onStateChange?: (state: VoiceEngineState) => void;
  /** Amostras de qualidade, para enviar à telemetria. */
  onStats?: (samples: Awaited<ReturnType<VoiceMesh['collectStats']>>) => void;
}

export class VoiceEngine {
  private readonly gateway: GatewayClient;
  private readonly options: VoiceEngineOptions;

  private mesh: VoiceMesh | null = null;
  private pipeline: AudioPipeline | null = null;
  private governor: BandwidthGovernor | null = null;

  private micTrack: LocalTrack | null = null;
  private cameraTrack: LocalTrack | null = null;
  private readonly screenTracks = new Map<string, LocalTrack>();

  private readonly remoteTracks = new Map<string, RemoteTrack[]>();
  private readonly unsubscribes: Array<() => void> = [];
  private statsTimer: ReturnType<typeof setInterval> | undefined;

  private micEnabled = true;
  private deafened = false;
  private notice: string | null = null;
  private bandwidth: AllocationDecision | null = null;
  private audioLevel: AudioLevel = { rms: 0, peak: 0, gateOpen: false };

  /* ------------------------------------------------------------------------
     Reentrada automática
     ------------------------------------------------------------------------
     `desiredChannelId` é a INTENÇÃO, e é diferente de `gateway.voice`, que é o
     fato. A distinção é o que permite reentrar: depois de uma queda o fato
     desaparece, e sem guardar a intenção não há como saber que a pessoa QUERIA
     estar numa chamada.

     Ela é limpa só em `leave()` e `dispose()` — ou seja, apenas quando a pessoa
     ou o app decidem sair. Queda de rede não é decisão de ninguém.
  ------------------------------------------------------------------------ */
  private desiredChannelId: string | null = null;
  private rejoinAttempt = 0;
  private rejoinTimer: ReturnType<typeof setTimeout> | null = null;
  /** `true` enquanto uma tentativa de reentrada está em curso. */
  private rejoining = false;

  /* ---- Quem está falando ---- */
  private readonly speaking = new SpeakingDetector();
  private speakingTimer: ReturnType<typeof setInterval> | null = null;
  /** Sessões que estão falando, para o `state` ler sem amostrar. */
  private speakingNow = new Map<string, number>();
  private audioSettings: AudioSettings;

  constructor(options: VoiceEngineOptions) {
    this.gateway = options.gateway;
    this.options = options;
    this.audioSettings = options.audioSettings ?? DEFAULT_AUDIO_SETTINGS;
    this.subscribeGateway();
  }

  /* ==========================================================================
     Estado exposto
     ========================================================================== */

  get state(): VoiceEngineState {
    const voice = this.gateway.voice;

    const participants: VoiceParticipant[] = [];

    if (voice !== null) {
      // O próprio usuário entra na lista, marcado. A interface precisa dele para
      // mostrar o medidor e os controles no mesmo lugar dos outros — separar
      // "eu" dos "outros" duplicaria o componente de participante.
      participants.push({
        userId: 'self',
        sessionId: voice.sessionId,
        isSelf: true,
        selfMuted: !this.micEnabled,
        selfDeafened: this.deafened,
        cameraOn: this.cameraTrack !== null,
        screenCount: this.screenTracks.size,
        tracks: [],
        connectionState: 'self',
        unstable: false,
        /*
         * O próprio "falando" vem do PIPELINE, não do RTP.
         *
         * `gateOpen` é o veredito do noise gate: ele já decidiu que aquilo é voz
         * e não ruído de teclado, com os parâmetros que a pessoa configurou.
         * Reaproveitá-lo é mais preciso que um limiar sobre o RMS — e é o mesmo
         * critério que decide se o áudio é transmitido, então o indicador
         * concorda com o que os outros ouvem.
         *
         * Mudo zera: um indicador aceso com o microfone fechado seria mentira.
         */
        speaking: this.micEnabled && this.audioLevel.gateOpen,
        speakingLevel: this.micEnabled ? this.audioLevel.rms : 0,
      });

      for (const peer of voice.peers.values()) {
        const meshPeer = this.mesh?.peerList.find((p) => p.sessionId === peer.session_id);
        participants.push({
          userId: peer.user_id,
          sessionId: peer.session_id,
          username: peer.username,
          displayName: peer.display_name,
          isSelf: false,
          selfMuted: peer.self_mute,
          selfDeafened: peer.self_deaf,
          cameraOn: peer.camera_on,
          screenCount: peer.screen_count,
          tracks: this.remoteTracks.get(peer.session_id) ?? [],
          connectionState: meshPeer?.connectionState ?? 'new',
          /*
           * "Instável" é diferente de "desconectado".
           *
           * `disconnected` é frequentemente transitório, e mostrar o par como
           * perdido faria a interface piscar em toda troca de rota. `unstable`
           * mostra um aviso discreto sem remover ninguém da lista.
           */
          unstable:
            meshPeer?.connectionState === 'disconnected' ||
            meshPeer?.iceState === 'disconnected',
          /*
           * `self_mute` VENCE o nível medido.
           *
           * Há uma janela de alguns pacotes entre a pessoa apertar mudo e a
           * última rajada de áudio já em trânsito chegar. Sem esta precedência,
           * o indicador ficaria aceso por um instante depois do mudo — e o mudo
           * é justamente o momento em que a pessoa está olhando para confirmar
           * que funcionou.
           */
          speaking: peer.self_mute ? false : this.speakingNow.has(peer.session_id),
          speakingLevel: peer.self_mute
            ? 0
            : (this.speakingNow.get(peer.session_id) ?? 0),
        });
      }
    }

    return {
      channelId: voice?.channelId ?? null,
      connected: voice !== null,
      participants,
      micEnabled: this.micEnabled,
      deafened: this.deafened,
      cameraOn: this.cameraTrack !== null,
      camera: this.cameraTrack,
      screens: [...this.screenTracks.values()],
      audioLevel: this.audioLevel,
      bandwidth: this.bandwidth,
      notice: this.notice,
      maxParticipants: voice?.maxParticipants ?? 8,
    };
  }

  private publish(): void {
    this.options.onStateChange?.(this.state);
  }

  /* ==========================================================================
     Eventos do gateway
     ========================================================================== */

  private subscribeGateway(): void {
    this.unsubscribes.push(
      this.gateway.on('VOICE_READY', (data) => {
        void this.onVoiceReady(data);
      }),

      /*
       * Conexão de volta: se a pessoa QUERIA estar em chamada, volta para ela.
       * ====================================================================
       * `READY` é sessão NOVA — o servidor apagou `voice_states` no `close` do
       * socket anterior e o roster da sala já não tem ninguém deste lado. É o
       * caso que precisa de reentrada.
       *
       * `RESUMED` é a sessão anterior recuperada, e aí normalmente não há nada
       * a fazer: o estado de voz sobreviveu. O teste de `gateway.voice === null`
       * cobre o caso em que não sobreviveu, em vez de assumir.
       *
       * Vale lembrar por que isso é necessário mesmo com mídia P2P: as
       * `RTCPeerConnection` de fato sobrevivem à queda do gateway, e é assim que
       * a chamada continua. Mas do lado do SERVIDOR a pessoa saiu da sala —
       * então quem entrar depois não a vê no roster, e ela não recebe sinalização
       * de mais ninguém. Sem reentrar, a chamada existe só para quem já estava
       * dentro, e vai morrendo participante por participante.
       */
      this.gateway.on('READY', () => this.considerRejoin()),
      this.gateway.on('RESUMED', () => this.considerRejoin()),

      /* ====================================================================
         O socket caiu e levou a sala junto
         ====================================================================
         O `GatewayClient` limpa a própria `voice` no fechamento — participar de
         uma sala é estado de SESSÃO no servidor, e um socket novo é uma sessão
         nova. Aqui só se reflete isso na interface.

         A malha NÃO é derrubada. As `RTCPeerConnection` sobrevivem à queda do
         gateway, e é assim que a conversa continua enquanto o servidor volta —
         desmontá-las aqui transformaria uma queda de 3 segundos do gateway num
         corte de áudio para todo mundo.

         `desiredChannelId` continua apontando para o canal, e é ele que faz a
         reentrada acontecer no próximo READY.
      ==================================================================== */
      this.gateway.on('VOICE_SESSION_LOST', () => {
        if (this.desiredChannelId !== null) {
          this.rejoining = true;
          this.notice = 'Conexão perdida. Voltando para a chamada assim que possível…';
        }
        this.publish();
      }),

      this.gateway.on('VOICE_PEER_JOIN', (data) => {
        const payload = data as { peer: VoicePeer };
        this.mesh?.addPeer(payload.peer.user_id, payload.peer.session_id);
        this.publish();
      }),

      this.gateway.on('VOICE_PEER_LEAVE', (data) => {
        const payload = data as { session_id: string; reason?: string };
        this.mesh?.removePeer(payload.session_id, payload.reason ?? 'saiu');
        this.remoteTracks.delete(payload.session_id);
        this.publish();
      }),

      this.gateway.on('VOICE_STATE_UPDATE', () => {
        // O estado do par já foi aplicado no `GatewayClient`; aqui só reflete.
        this.publish();
      }),

      this.gateway.on('SIGNAL', (data) => {
        const payload = data as {
          from_user_id: string;
          from_session_id: string;
          payload: Parameters<VoiceMesh['handleSignal']>[2];
        };
        void this.mesh?.handleSignal(
          payload.from_user_id,
          payload.from_session_id,
          payload.payload,
        );
      }),

      this.gateway.on('SIGNAL_ERROR', (data) => {
        /*
         * Erro de relay significa que o par saiu.
         *
         * Sem tratar, a RTCPeerConnection fica em `connecting` para sempre
         * ocupando recurso, e a interface mostra alguém que não está mais lá.
         */
        const payload = data as { to_session_id?: string; error?: string };
        if (payload.to_session_id) {
          this.mesh?.removePeer(payload.to_session_id, payload.error ?? 'inalcançável');
          this.remoteTracks.delete(payload.to_session_id);
          this.publish();
        }
      }),

      this.gateway.on('VOICE_MOVED', (data) => {
        void this.onVoiceMoved(data);
      }),

      this.gateway.on('VOICE_ERROR', (data) => {
        const payload = data as { message?: string };
        this.notice = payload.message ?? 'Erro na chamada.';
        this.publish();
      }),
    );
  }

  /* ==========================================================================
     Entrar e sair
     ========================================================================== */

  /**
   * Entra num canal de voz.
   *
   * DEVE ser chamado dentro do gesto do usuário (handler de clique). O
   * `AudioContext` nasce suspenso pela política de autoplay e só sai disso num
   * gesto — chamar isto num `useEffect` produz uma chamada em que ninguém
   * ouve ninguém, sem nenhum erro visível.
   */
  async join(channelId: string): Promise<void> {
    const capabilities = detectCapabilities();
    if (!capabilities.isSecureContext) {
      this.notice =
        'Microfone exige HTTPS. Abra o Concord pelo endereço seguro para entrar ' +
        'em chamadas.';
      this.publish();
      return;
    }

    /*
     * A intenção é registrada ANTES de qualquer coisa poder falhar.
     *
     * Se o microfone for negado ou o socket estiver fora, a intenção continua
     * valendo — e é ela que faz a reentrada acontecer quando a conexão voltar,
     * em vez de a pessoa ficar olhando uma tela que diz que não está em chamada
     * nenhuma.
     */
    this.desiredChannelId = channelId;

    /*
     * O pipeline é REAPROVEITADO quando já existe.
     *
     * Numa reentrada automática o microfone já está aberto e a cadeia de áudio
     * montada. Recriar chamaria `getUserMedia` de novo fora de um gesto do
     * usuário — o que em alguns navegadores falha, e nos outros pisca a luz da
     * webcam/microfone do sistema sem motivo. Reusar também preserva os
     * parâmetros do gate e do RNNoise que a pessoa ajustou.
     */
    if (this.pipeline !== null && this.micTrack !== null) {
      const sentRejoin = this.gateway.joinVoice(channelId, {
        selfMute: !this.micEnabled,
        selfDeaf: this.deafened,
      });
      if (!sentRejoin) {
        this.notice = 'Sem conexão com o servidor. Tentando novamente…';
        this.publish();
      }
      return;
    }

    // 1. Áudio primeiro, ainda dentro do gesto.
    this.pipeline = new AudioPipeline();
    try {
      const track = await this.pipeline.start(this.audioSettings);
      this.micTrack = {
        track,
        purpose: 'microphone',
        sourceId: 'microphone',
        label: 'Microfone',
      };
      this.unsubscribes.push(
        this.pipeline.onLevel((level) => {
          this.audioLevel = level;
          // Não publica o estado inteiro a cada quadro de medidor: 90 Hz de
          // re-render derrubaria a interface. A UI assina o nível separadamente.
        }),
      );
    } catch (error) {
      this.notice =
        error instanceof Error && error.name === 'NotAllowedError'
          ? 'Permissão de microfone negada. Libere o acesso para entrar na chamada.'
          : 'Não foi possível acessar o microfone.';
      await this.pipeline.stop();
      this.pipeline = null;
      this.publish();
      return;
    }

    // 2. Gateway: pede entrada e espera VOICE_READY, que traz roster e ICE.
    const sent = this.gateway.joinVoice(channelId, {
      selfMute: !this.micEnabled,
      selfDeaf: this.deafened,
    });

    if (!sent) {
      this.notice = 'Sem conexão com o servidor. Não é possível entrar na chamada.';
      await this.pipeline.stop();
      this.pipeline = null;
      this.micTrack = null;
      this.publish();
      return;
    }

    this.publish();
  }

  /**
   * Monta a malha quando o roster chega.
   *
   * Separado de `join` porque `VOICE_READY` é assíncrono e pode chegar depois de
   * uma reconexão — nesse caso a malha precisa ser reconstruída sem o usuário ter
   * clicado em nada.
   */
  private async onVoiceReady(data: unknown): Promise<void> {
    const payload = data as {
      channel_id: string;
      session_id: string;
      peers: VoicePeer[];
      max_participants: number;
    };

    const voice = this.gateway.voice;
    if (voice === null) return;

    /*
     * A malha ANTERIOR é descartada, e isto não é zelo excessivo.
     *
     * `onVoiceReady` chega também numa reentrada, e antes ele criava uma malha
     * nova em cima da referência antiga. As `RTCPeerConnection` da malha velha
     * ficavam abertas, sem ninguém as fechando: cada reconexão vazava N−1
     * conexões, com seus transceivers, encoders e sockets ICE. Numa rede
     * instável isso acumula até o navegador recusar novas conexões.
     *
     * O governador também precisa parar: dois governadores medindo a mesma
     * banda tomam decisões contraditórias sobre as mesmas faixas.
     */
    if (this.mesh !== null) {
      this.mesh.dispose();
      this.mesh = null;
    }
    this.governor?.stop();
    this.governor = null;
    /*
     * As faixas remotas somem junto.
     *
     * Elas pertencem às sessões da malha antiga, e depois de uma reconexão os
     * ids de sessão são outros. Mantê-las deixaria participantes fantasmas na
     * tela — presentes na lista, sem nunca receber quadro.
     */
    this.remoteTracks.clear();
    this.speaking.reset();
    this.speakingNow.clear();

    // 3. Malha com as credenciais ICE recebidas.
    this.mesh = new VoiceMesh({
      selfSessionId: payload.session_id,
      iceServers: voice.iceServers,
      transport: {
        signal: (toUserId, toSessionId, signalPayload) =>
          this.gateway.signal(toUserId, toSessionId, signalPayload),
      },
      events: {
        onRemoteTrack: (track) => {
          const list = this.remoteTracks.get(track.peerSessionId) ?? [];
          // Substitui um track do mesmo id em vez de acumular: renegociação
          // reenvia tracks, e acumular produziria vídeos duplicados na tela.
          const filtered = list.filter((t) => t.track.id !== track.track.id);
          filtered.push(track);
          this.remoteTracks.set(track.peerSessionId, filtered);
          this.publish();
        },
        onRemoteTrackEnded: (peerSessionId, trackId) => {
          const list = this.remoteTracks.get(peerSessionId) ?? [];
          this.remoteTracks.set(
            peerSessionId,
            list.filter((t) => t.track.id !== trackId),
          );
          this.publish();
        },
        onPeerStateChange: () => this.publish(),
        onControlMessage: (peerSessionId, message) =>
          this.onControlMessage(peerSessionId, message),
        onPeerLost: (peerSessionId, reason) => {
          this.remoteTracks.delete(peerSessionId);
          this.notice = `Conexão com um participante foi perdida: ${reason}.`;
          this.publish();
        },
      },
    });

    // 4. Anexa o microfone e abre um par por integrante do roster.
    if (this.micTrack) {
      this.micTrack.track.enabled = this.micEnabled;
      this.mesh.addLocalTrack(this.micTrack);
    }

    /*
     * Câmera e telas voltam também.
     *
     * Isto só importa numa REENTRADA, e é justamente o caso que faltava: a
     * versão anterior reanexava apenas o microfone. Quem estava compartilhando
     * a tela quando a conexão caiu voltava para a chamada sem estar
     * compartilhando nada — e sem nenhum aviso, porque o botão continuava
     * aceso: o estado local dizia que a tela existia, e existia mesmo; ela só
     * não estava mais ligada a par nenhum.
     */
    if (this.cameraTrack) {
      this.mesh.addLocalTrack(this.cameraTrack);
    }
    for (const screen of this.screenTracks.values()) {
      this.mesh.addLocalTrack(screen);
    }

    for (const peer of payload.peers) {
      this.mesh.addPeer(peer.user_id, peer.session_id);
    }

    // 5. Governador por último: agora há malha e tracks para governar.
    this.governor = new BandwidthGovernor({
      budget: {
        userCeilingKbps: this.options.uploadCeilingKbps ?? null,
        estimatedKbps: null,
        // Piso de 500 kbps: abaixo disso nem o áudio de uma malha pequena cabe,
        // e reportar orçamento menor faria o governador pausar tudo, inclusive
        // a primeira tela — o que não ajuda ninguém.
        floorKbps: 500,
      },
      onSourcePaused: (sourceId, paused, reason) => {
        const label = this.screenTracks.get(sourceId)?.label ?? sourceId;
        this.notice = paused
          ? `${label} pausada: ${reason}`
          : `${label} retomada.`;
        this.publish();
      },
      onDecision: (decision) => {
        this.bandwidth = decision;
        if (decision.notice !== null) this.notice = decision.notice;
        this.publish();
      },
    });

    this.governor.start(() => ({
      peers: this.mesh?.peerList ?? [],
      localTracks: this.mesh?.localTrackList ?? [],
    }));

    /* ---- Entrou: o contador de tentativas volta a zero ---- */
    const wasRejoining = this.rejoining;
    this.rejoining = false;
    this.rejoinAttempt = 0;
    if (this.rejoinTimer !== null) {
      clearTimeout(this.rejoinTimer);
      this.rejoinTimer = null;
    }
    /*
     * O aviso só é limpo se ERA de reentrada.
     *
     * Zerar sempre apagaria uma mensagem legítima do governador de banda que
     * acabou de chegar — "tela 2 pausada" some no instante em que a pessoa
     * precisava lê-la.
     */
    if (wasRejoining) {
      this.notice = 'Reconectado à chamada.';
    }

    this.startSpeakingLoop();

    this.startStatsCollection();
    this.publish();
  }

  private async onVoiceMoved(data: unknown): Promise<void> {
    const payload = data as { to_channel_id: string; peers: VoicePeer[] };

    /*
     * Movido pela moderação: descarta a malha inteira e reconstrói.
     *
     * Reaproveitar os pares seria manter áudio fluindo para gente da sala
     * anterior — pessoas que não fazem parte da conversa nova. As telas também
     * são descartadas, pelo mesmo motivo: elas foram compartilhadas para um
     * público específico.
     */
    this.mesh?.dispose();
    this.remoteTracks.clear();

    for (const screen of this.screenTracks.values()) screen.track.stop();
    this.screenTracks.clear();

    if (this.cameraTrack) {
      this.cameraTrack.track.stop();
      this.cameraTrack = null;
    }

    this.notice = 'Você foi movido para outro canal de voz pela moderação.';

    await this.onVoiceReady({
      channel_id: payload.to_channel_id,
      session_id: this.gateway.voice?.sessionId ?? '',
      peers: payload.peers,
      max_participants: this.gateway.voice?.maxParticipants ?? 8,
    });
  }

  private onControlMessage(peerSessionId: string, message: ControlMessage): void {
    /*
     * O canal de controle é a fonte de verdade quando o gateway está fora.
     *
     * Com o gateway vivo, `VOICE_STATE_UPDATE` chega antes e este caminho é
     * redundante — e a redundância é o ponto: é ela que faz a chamada continuar
     * usável depois de o servidor cair.
     */
    if (message.t === 'mute' || message.t === 'deaf') {
      const voice = this.gateway.voice;
      const peer = voice?.peers.get(peerSessionId);
      if (voice && peer) {
        voice.peers.set(peerSessionId, {
          ...peer,
          ...(message.t === 'mute' ? { self_mute: message.muted } : {}),
          ...(message.t === 'deaf' ? { self_deaf: message.deafened } : {}),
        });
        this.publish();
      }
    }
  }

  /**
   * Envia amostras de qualidade a cada 10 segundos.
   *
   * 10s e não 2s: a telemetria é para diagnóstico posterior, não para reação em
   * tempo real — o governador já reage a 2s com dados locais. Amostrar a cada 2s
   * multiplicaria o volume em `rum.call_quality` por cinco sem melhorar nenhum
   * diagnóstico.
   */
  private startStatsCollection(): void {
    this.stopStatsCollection();
    if (!this.options.onStats) return;

    this.statsTimer = setInterval(() => {
      void (async () => {
        const samples = await this.mesh?.collectStats();
        if (samples && samples.length > 0) this.options.onStats?.(samples);
      })();
    }, 10_000);
  }

  private stopStatsCollection(): void {
    if (this.statsTimer !== undefined) {
      clearInterval(this.statsTimer);
      this.statsTimer = undefined;
    }
  }

  /**
   * Liga o laço que lê o nível de áudio de cada par.
   *
   * 10 Hz. Acima disso não se percebe diferença — a sensação de "acendeu junto
   * com a voz" satura por volta de 100 ms — e cada amostra é uma varredura dos
   * receivers de todos os pares.
   */
  private startSpeakingLoop(): void {
    if (this.speakingTimer !== null) return;

    this.speakingTimer = setInterval(() => {
      const mesh = this.mesh;
      if (!mesh) return;

      /*
       * Só os receivers de ÁUDIO.
       *
       * `getSynchronizationSources()` num receiver de vídeo devolve fontes sem
       * `audioLevel`, então o resultado seria zero — mas varrer sete pares ×
       * três transceivers de vídeo a 10 Hz é trabalho por nada. Numa sala de
       * oito com duas telas cada, é a diferença entre 70 e 400 leituras por
       * segundo.
       */
      const receivers: Array<readonly [string, RTCRtpReceiver]> = [];
      for (const peer of mesh.peerList) {
        for (const transceiver of peer.connection.getTransceivers()) {
          if (transceiver.receiver.track?.kind !== 'audio') continue;
          receivers.push([peer.sessionId, transceiver.receiver]);
        }
      }

      const changes = this.speaking.sample(receivers, Date.now());
      if (changes.length === 0) return;

      for (const change of changes) {
        if (change.speaking) {
          this.speakingNow.set(change.sessionId, change.level);
        } else {
          this.speakingNow.delete(change.sessionId);
        }
      }

      /*
       * `publish()` só quando ALGO mudou.
       *
       * `sample()` já devolve apenas as mudanças, com histerese e um passo
       * mínimo de nível. Publicar a cada tique faria a árvore React re-renderizar
       * 10 vezes por segundo durante a chamada inteira — exatamente quando a CPU
       * está codificando vídeo para todos os pares.
       */
      this.publish();
    }, 100);

    /*
     * `unref` onde existe.
     *
     * No navegador `setInterval` devolve um número e não tem `unref`; no Node
     * (onde os testes rodam) um timer sem `unref` segura o processo vivo e o
     * teste nunca termina.
     */
    (this.speakingTimer as unknown as { unref?: () => void }).unref?.();
  }

  private stopSpeakingLoop(): void {
    if (this.speakingTimer !== null) {
      clearInterval(this.speakingTimer);
      this.speakingTimer = null;
    }
    this.speaking.reset();
    this.speakingNow.clear();
  }

  /**
   * Decide se vale reentrar, e agenda.
   *
   * Chamado a cada `READY`/`RESUMED`. As três saídas antecipadas cobrem:
   * ninguém quer estar em chamada, já está em uma, ou já há tentativa agendada.
   */
  private considerRejoin(): void {
    if (this.desiredChannelId === null) return;
    if (this.gateway.voice !== null) return;
    if (this.rejoinTimer !== null) return;

    /*
     * Teto de tentativas, e o motivo de existir um.
     *
     * Sem teto, um canal apagado ou uma sala cheia produzem um laço infinito de
     * VOICE_JOIN → VOICE_ERROR → JOIN, com o cliente batendo no servidor para
     * sempre. Depois de 5 tentativas o aviso passa a pedir um clique — que é a
     * informação honesta: "não consegui, tente você".
     */
    if (this.rejoinAttempt >= 5) {
      this.notice =
        'Não foi possível voltar para a chamada. Clique no canal para entrar de novo.';
      this.publish();
      return;
    }

    /*
     * Espera crescente COM jitter.
     *
     * Sem jitter, todos os clientes derrubados pelo mesmo deploy voltam no mesmo
     * instante — e o servidor que acabou de subir recebe a sala inteira de uma
     * vez, o que é a forma mais barata de derrubá-lo outra vez.
     *
     * A primeira tentativa espera ~600 ms: o `READY` acabou de chegar, e um
     * VOICE_JOIN imediato às vezes chega antes de o servidor terminar de
     * registrar a sessão nova.
     */
    const base = 600 * 2 ** this.rejoinAttempt;
    const delay = Math.min(base + Math.random() * base, 15_000);

    this.rejoinAttempt += 1;
    this.rejoining = true;
    this.notice =
      this.rejoinAttempt === 1
        ? 'Conexão restabelecida. Voltando para a chamada…'
        : `Voltando para a chamada… (tentativa ${this.rejoinAttempt} de 5)`;
    this.publish();

    this.rejoinTimer = setTimeout(() => {
      this.rejoinTimer = null;
      const channelId = this.desiredChannelId;
      /*
       * A intenção é relida DENTRO do timer.
       *
       * Entre agendar e disparar a pessoa pode ter clicado em sair, e reentrar
       * numa chamada que ela acabou de abandonar é o pior tipo de automação.
       */
      if (channelId === null) {
        this.rejoining = false;
        return;
      }
      void this.join(channelId);
    }, delay);

    /* `unref` onde existe: no Node um timer pendurado impede o processo de sair. */
    (this.rejoinTimer as unknown as { unref?: () => void }).unref?.();
  }

  async leave(): Promise<void> {
    /*
     * Sair é uma DECISÃO, e ela cancela a reentrada.
     *
     * Sem isto, sair no meio de uma reconexão deixaria um timer agendado que
     * arrastaria a pessoa de volta para a chamada segundos depois.
     */
    this.desiredChannelId = null;
    this.rejoinAttempt = 0;
    this.rejoining = false;
    if (this.rejoinTimer !== null) {
      clearTimeout(this.rejoinTimer);
      this.rejoinTimer = null;
    }

    this.gateway.leaveVoice();
    this.stopSpeakingLoop();
    this.stopStatsCollection();
    this.governor?.stop();
    this.governor = null;

    this.mesh?.dispose();
    this.mesh = null;
    this.remoteTracks.clear();

    for (const screen of this.screenTracks.values()) screen.track.stop();
    this.screenTracks.clear();

    if (this.cameraTrack) {
      this.cameraTrack.track.stop();
      this.cameraTrack = null;
    }

    await this.pipeline?.stop();
    this.pipeline = null;
    this.micTrack = null;

    this.bandwidth = null;
    this.audioLevel = { rms: 0, peak: 0, gateOpen: false };
    this.publish();
  }

  /* ==========================================================================
     Controles
     ========================================================================== */

  toggleMicrophone(): void {
    this.micEnabled = !this.micEnabled;
    this.mesh?.setMicrophoneEnabled(this.micEnabled);
    this.gateway.updateVoiceState({ selfMute: !this.micEnabled });
    this.publish();
  }

  toggleDeafen(): void {
    this.deafened = !this.deafened;
    /*
     * Ensurdecer também SILENCIA o microfone, e não é só convenção.
     *
     * Quem não ouve a sala não sabe se está atropelando alguém, nem se o que
     * disse foi entendido. Continuar transmitindo nessa condição é ruído para
     * os outros. Desmutar depois é um clique.
     */
    if (this.deafened && this.micEnabled) {
      this.micEnabled = false;
      this.mesh?.setMicrophoneEnabled(false);
    }
    this.gateway.updateVoiceState({
      selfDeaf: this.deafened,
      selfMute: !this.micEnabled,
    });
    this.publish();
  }

  /**
   * Pergunta se cabe uma tela nova, ANTES de abrir o seletor.
   *
   * A ordem importa: abrir o diálogo do sistema e depois dizer "não cabe" é
   * desrespeitoso com o tempo de quem clicou. O aviso vem primeiro, com números.
   */
  canAddScreen(): { allowed: boolean; warning: string | null } {
    if (!this.governor || !this.mesh) {
      return { allowed: false, warning: 'Entre em uma chamada primeiro.' };
    }
    return this.governor.canAddScreen({
      peerCount: this.mesh.peerCount,
      localTracks: this.mesh.localTrackList,
    });
  }

  /** Adiciona uma tela. Cada chamada abre um seletor do sistema. */
  async addScreen(options: Partial<CaptureOptions> = {}): Promise<
    { ok: true; sourceId: string } | { ok: false; reason: string; cancelled: boolean }
  > {
    if (!this.mesh) {
      return { ok: false, reason: 'Você não está em uma chamada.', cancelled: false };
    }

    try {
      const result = await captureScreen(options, (sourceId) => {
        // O usuário parou pela barra do navegador, fora do nosso código.
        void this.removeScreen(sourceId);
      });

      this.screenTracks.set(result.video.sourceId, result.video);
      this.mesh.addLocalTrack(result.video);

      if (result.systemAudio) {
        this.screenTracks.set(result.systemAudio.sourceId, result.systemAudio);
        this.mesh.addLocalTrack(result.systemAudio);
      }
      if (result.systemAudioNotice !== null) {
        this.notice = result.systemAudioNotice;
      }

      this.gateway.updateVoiceState({
        screenCount: [...this.screenTracks.values()].filter(
          (t) => t.purpose === 'screen',
        ).length,
      });
      this.publish();
      return { ok: true, sourceId: result.video.sourceId };
    } catch (error) {
      if (error instanceof CaptureCancelled) {
        // Cancelamento NÃO é erro e não deve produzir aviso na interface: a
        // pessoa mudou de ideia, e uma mensagem faria parecer que algo falhou.
        return { ok: false, reason: 'cancelado', cancelled: true };
      }
      const reason =
        error instanceof Error ? error.message : 'Não foi possível capturar a tela.';
      this.notice = reason;
      this.publish();
      return { ok: false, reason, cancelled: false };
    }
  }

  async removeScreen(sourceId: string): Promise<void> {
    const track = this.screenTracks.get(sourceId);
    if (!track) return;

    track.track.stop();
    this.screenTracks.delete(sourceId);
    this.mesh?.removeLocalTrack(sourceId);

    // O áudio do sistema acompanha a tela: manter o áudio de uma tela que já não
    // é compartilhada seria captura sem contexto.
    const audioId = `${sourceId}-audio`;
    const audio = this.screenTracks.get(audioId);
    if (audio) {
      audio.track.stop();
      this.screenTracks.delete(audioId);
      this.mesh?.removeLocalTrack(audioId);
    }

    this.gateway.updateVoiceState({
      screenCount: [...this.screenTracks.values()].filter((t) => t.purpose === 'screen')
        .length,
    });
    this.publish();
  }

  async toggleCamera(deviceId: string | null = null): Promise<void> {
    if (!this.mesh) return;

    if (this.cameraTrack !== null) {
      this.cameraTrack.track.stop();
      this.mesh.removeLocalTrack(this.cameraTrack.sourceId);
      this.cameraTrack = null;
      this.gateway.updateVoiceState({ cameraOn: false });
      this.publish();
      return;
    }

    try {
      const track = await captureCamera(deviceId);
      this.cameraTrack = track;
      this.mesh.addLocalTrack(track);
      this.gateway.updateVoiceState({ cameraOn: true });
    } catch (error) {
      this.notice =
        error instanceof Error && error.name === 'NotAllowedError'
          ? 'Permissão de câmera negada.'
          : 'Não foi possível acessar a câmera.';
    }
    this.publish();
  }

  /** Assina o medidor de nível separadamente do estado geral. */
  onAudioLevel(listener: (level: AudioLevel) => void): () => void {
    if (!this.pipeline) return () => undefined;
    return this.pipeline.onLevel(listener);
  }

  /** Atualiza a configuração de áudio, reconstruindo a cadeia se necessário. */
  async updateAudioSettings(patch: Partial<AudioSettings>): Promise<void> {
    this.audioSettings = { ...this.audioSettings, ...patch };
    if (!this.pipeline) return;

    const { requiresRestart } = this.pipeline.update(patch);
    if (!requiresRestart) return;

    /*
     * Troca de dispositivo usa `replaceTrack`, não renegociação.
     *
     * A cadeia é reconstruída (é preciso: a topologia mudou), mas o track novo
     * SUBSTITUI o antigo nos senders existentes. Renegociar com sete pares para
     * trocar de microfone produziria vários segundos de corte — e trocar de
     * microfone é algo que se faz justamente quando o áudio está ruim.
     */
    const track = await this.pipeline.restart();
    track.enabled = this.micEnabled;

    if (this.micTrack) {
      this.micTrack = { ...this.micTrack, track };
      await this.mesh?.replaceLocalTrack('microphone', track);
    }
    this.publish();
  }

  setUploadCeiling(kbps: number | null): void {
    this.governor?.setUserCeiling(kbps);
  }

  /** Descarta tudo. Chamado no logout ou ao desmontar a aplicação. */
  async dispose(): Promise<void> {
    for (const unsubscribe of this.unsubscribes) unsubscribe();
    this.unsubscribes.length = 0;
    await this.leave();
  }
}
